import { useEffect, useState, useCallback, useMemo } from "react";
import { API } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "./ui/dialog";
import {
  X, CaretLeft, CaretRight, ArrowSquareOut, MagnifyingGlassPlus, MagnifyingGlassMinus,
  ArrowsClockwise, FileText, Image as ImageIcon, DownloadSimple,
} from "@phosphor-icons/react";

/**
 * Reusable in-browser document viewer.
 *
 * Props:
 *   - open: boolean
 *   - onClose: () => void
 *   - docs: array of {id, display_filename, original_filename, content_type}
 *   - startId: id of doc to open first
 *
 * Supports prev/next when `docs` has more than one entry.
 * Renders images inline, PDFs in an iframe (browser-native PDF viewer).
 * Falls back to a "open in new tab" link for unrenderable types.
 */
export default function DocumentViewer({ open, onClose, docs = [], startId = null }) {
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const [idx, setIdx] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [rotate, setRotate] = useState(0);

  useEffect(() => {
    if (!open) return;
    const i = startId ? Math.max(0, docs.findIndex((d) => d.id === startId)) : 0;
    setIdx(i);
    setZoom(1);
    setRotate(0);
  }, [open, startId, docs]);

  const current = docs[idx];

  const next = useCallback(() => {
    if (docs.length <= 1) return;
    setIdx((i) => (i + 1) % docs.length);
    setZoom(1); setRotate(0);
  }, [docs.length]);

  const prev = useCallback(() => {
    if (docs.length <= 1) return;
    setIdx((i) => (i - 1 + docs.length) % docs.length);
    setZoom(1); setRotate(0);
  }, [docs.length]);

  // Keyboard navigation
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "ArrowRight") next();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, next, prev, onClose]);

  const url = current ? `${API}/documents/${current.id}/download` : "";
  const ct = (current?.content_type || "").toLowerCase();
  const fname = (current?.original_filename || current?.display_filename || "").toLowerCase();
  // Treat any image MIME or common image extensions as image (covers heic/heif/webp/avif/jfif).
  const isImage = ct.startsWith("image/") ||
    /\.(png|jpe?g|gif|webp|heic|heif|bmp|tiff?|avif|jfif)$/i.test(fname);
  const isPdf = ct.includes("pdf") || fname.endsWith(".pdf");
  const isHeic = ct.includes("heic") || ct.includes("heif") || /\.(heic|heif)$/i.test(fname);
  const isEmail = /\.(eml|msg)$/i.test(fname) || ct.includes("rfc822") || ct.includes("ms-outlook");
  const isText = ct.startsWith("text/") || /\.(txt|csv|log)$/i.test(fname);
  const filename = current?.display_filename || current?.original_filename || "document";

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent
        className="rounded-none w-[95vw] max-w-[1200px] h-[92vh] flex flex-col p-0"
        data-testid="document-viewer"
      >
        <DialogHeader className="p-4 border-b border-[#E5E5E5] flex-row items-center justify-between gap-3 space-y-0">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            {isImage ? <ImageIcon size={18} weight="duotone" className="text-[#10B981] flex-shrink-0" /> :
              <FileText size={18} weight="duotone" className="text-[#002FA7] flex-shrink-0" />}
            <DialogTitle className="font-heading text-base truncate" data-testid="viewer-filename">
              {filename}
            </DialogTitle>
            {docs.length > 1 && (
              <span className="label-tag text-[10px] text-[#525252] flex-shrink-0">
                {idx + 1} / {docs.length}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            {isImage && (
              <>
                <button onClick={() => setZoom((z) => Math.max(0.25, z - 0.25))} className="p-2 hover:bg-[#F5F5F5]" title="Zoom out" data-testid="viewer-zoom-out">
                  <MagnifyingGlassMinus size={16} weight="duotone" />
                </button>
                <span className="text-xs font-mono w-10 text-center">{Math.round(zoom * 100)}%</span>
                <button onClick={() => setZoom((z) => Math.min(4, z + 0.25))} className="p-2 hover:bg-[#F5F5F5]" title="Zoom in" data-testid="viewer-zoom-in">
                  <MagnifyingGlassPlus size={16} weight="duotone" />
                </button>
                <button onClick={() => setRotate((r) => (r + 90) % 360)} className="p-2 hover:bg-[#F5F5F5]" title="Rotate" data-testid="viewer-rotate">
                  <ArrowsClockwise size={16} weight="duotone" />
                </button>
                <span className="w-px h-5 bg-[#E5E5E5] mx-1" />
              </>
            )}
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 hover:bg-[#F5F5F5]"
              title="Open in new tab"
              data-testid="viewer-open-new-tab"
            >
              <ArrowSquareOut size={16} weight="duotone" />
            </a>
            {isOwner && current && (
              <a
                href={`${url}?mode=download`}
                download={filename}
                className="p-2 hover:bg-[#F5F5F5]"
                title="Download (owner only)"
                data-testid="viewer-download"
              >
                <DownloadSimple size={16} weight="duotone" />
              </a>
            )}
            <button onClick={onClose} className="p-2 hover:bg-[#F5F5F5]" title="Close" data-testid="viewer-close">
              <X size={18} weight="bold" />
            </button>
          </div>
        </DialogHeader>

        <div className="flex-1 relative bg-[#0A0A0A] overflow-hidden flex items-center justify-center">
          {/* Prev arrow */}
          {docs.length > 1 && (
            <button
              onClick={prev}
              className="absolute left-3 top-1/2 -translate-y-1/2 z-10 w-12 h-12 bg-white/90 hover:bg-white text-[#0A0A0A] flex items-center justify-center shadow-lg transition-all rounded-full"
              title="Previous (←)"
              data-testid="viewer-prev"
            >
              <CaretLeft size={22} weight="bold" />
            </button>
          )}

          {/* Document body */}
          {!current ? (
            <div className="text-white/60 text-sm">No file selected</div>
          ) : isImage ? (
            <div className="w-full h-full overflow-auto flex items-center justify-center p-4">
              <img
                src={url}
                alt={filename}
                style={{
                  transform: `scale(${zoom}) rotate(${rotate}deg)`,
                  transformOrigin: "center center",
                  transition: "transform 0.15s ease",
                  maxWidth: zoom <= 1 ? "100%" : "none",
                  maxHeight: zoom <= 1 ? "100%" : "none",
                }}
                data-testid="viewer-image"
              />
            </div>
          ) : isPdf ? (
            <iframe
              src={`${url}#view=FitH`}
              title={filename}
              className="w-full h-full border-0 bg-white"
              data-testid="viewer-pdf"
            />
          ) : isText ? (
            <iframe
              src={url}
              title={filename}
              className="w-full h-full border-0 bg-white"
              data-testid="viewer-text"
            />
          ) : (
            <div className="text-center p-8 text-white max-w-md">
              <FileText size={48} weight="duotone" className="mx-auto mb-3 opacity-60" />
              <div className="text-base mb-1">Inline preview isn&apos;t supported for this format.</div>
              <div className="text-xs text-white/60 mb-4">
                {isHeic && "HEIC photos from iPhone aren't always renderable in Chrome. "}
                {isEmail && "Email files (.eml/.msg) need to be opened in your mail client. "}
                Click below to open the file in a new tab — your browser&apos;s native handler will display it.
              </div>
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-white text-[#0A0A0A] hover:bg-[#F5F5F5] transition-all text-sm font-semibold"
                data-testid="viewer-open-new-tab"
              >
                <ArrowSquareOut size={14} weight="bold" /> Open in new tab
              </a>
            </div>
          )}

          {/* Next arrow */}
          {docs.length > 1 && (
            <button
              onClick={next}
              className="absolute right-3 top-1/2 -translate-y-1/2 z-10 w-12 h-12 bg-white/90 hover:bg-white text-[#0A0A0A] flex items-center justify-center shadow-lg transition-all rounded-full"
              title="Next (→)"
              data-testid="viewer-next"
            >
              <CaretRight size={22} weight="bold" />
            </button>
          )}
        </div>

        {/* Tiny footer hint */}
        {docs.length > 1 && (
          <div className="hidden md:block px-4 py-2 border-t border-[#E5E5E5] text-[11px] text-[#525252]">
            Use ← → arrow keys to navigate · Esc to close
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
